import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { LinkReservationToProfileBodyDto, schemas } from '@numa-group/reservation-v3-api';

import { Button, ButtonGroup } from '@src/components/atoms/inputs/button';
import { InputController } from '@src/components/input-controllers/input-controller';
import { Form } from '@src/components/molecules/form';
import { Modal } from '@src/components/molecules/modal';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { useErrorMessages } from '@src/hooks/use-error-messages';

import { RequestLinkReservationFormProps } from './types';

const RequestLinkReservationForm = ({ isLoading, onSubmit, errorMessage }: RequestLinkReservationFormProps) => {
  const {
    cancel: cancelLabel,
    linkReservationToProfileTwoStepFeature: { requestCtaLabel, reservationIdInputLabel },
  } = useCmsContent();

  const { handleSubmit, control, formState, setError } = useForm<LinkReservationToProfileBodyDto>({
    resolver: zodResolver(schemas.LinkReservationToProfileBodyDto),
    criteriaMode: 'all',
  });

  const errorMessages = useErrorMessages<LinkReservationToProfileBodyDto>(formState.errors);

  useEffect(() => {
    if (errorMessage) {
      setError(
        'reservationIdOrExternalId',
        {
          type: 'custom',
          message: errorMessage,
        },
        { shouldFocus: true },
      );
    }
  }, [errorMessage, setError]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <InputController
        name="reservationIdOrExternalId"
        autoComplete="off"
        error={errorMessages.reservationIdOrExternalId ?? formState.errors.reservationIdOrExternalId?.message}
        label={reservationIdInputLabel}
        control={control}
      />

      <ButtonGroup>
        <Modal.Close asChild>
          <Button size="small" variant="secondary" disabled={isLoading}>
            {cancelLabel}
          </Button>
        </Modal.Close>

        <Button size="small" type="submit" isLoading={isLoading}>
          {requestCtaLabel}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default RequestLinkReservationForm;
